import { createTheme } from '@mui/material';

import { claudeTokens } from './claudeTokens';

// 메인 테마 컬러 설정 — claudeTokens(SSOT)를 참조하여 단일 출처 유지
// 기존 `designSystem.colors.*` API 형태는 호환을 위해 그대로 두고, 값만 v2 톤(warm cream + coral)으로 swap
export const designSystem = {
  colors: {
    // 메인 브랜드 컬러 — coral (v2)
    primary: claudeTokens.color.accent,            // #5A9B96 (청록 — claudeTokens.ts 참조)
    primaryDark: claudeTokens.color.accentDark,    // #3D7E79
    primaryLight: claudeTokens.color.accentSoft,   // #D4E8E5

    // 그라디언트 — v2 톤에서는 그라데이션 제거하고 단색으로 통일
    gradient: {
      primary: claudeTokens.color.accent,
      primaryHover: claudeTokens.color.accentDark,
    },

    // 배경 색상
    background: {
      primary: claudeTokens.color.bg,        // #F5F8F7
      white: claudeTokens.color.surface,     // #FFFFFF
      light: claudeTokens.color.bgSunken,    // #EAF0EE (가라앉은 배경)
    },

    // 텍스트 색상
    text: {
      primary: claudeTokens.color.text1,     // #1F2A28
      secondary: claudeTokens.color.text2,   // #4F5B58
      light: claudeTokens.color.text3,       // #66736F
      white: '#ffffff',
    },

    // 보더 색상
    border: {
      light: claudeTokens.color.border,           // #E3E8E6
      medium: claudeTokens.color.borderStrong,    // #D3DBD8
      dark: claudeTokens.color.borderStrong,      // 다크 단계는 borderStrong로 매핑 (warm 톤에서 슬레이트 다크 부적합)
    },

    // 상태 색상
    status: {
      success: claudeTokens.color.status.success, // #2E7D32
      warning: claudeTokens.color.status.warning, // #9B7A1A
      error: claudeTokens.color.status.error,     // #B83A2E
      info: claudeTokens.color.status.info,       // #3B5BDB
    },

    // 그림자 — 기존 키 유지하되 warm 톤에 맞게 부드럽게
    shadow: {
      sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      base: '0 4px 6px rgba(0, 0, 0, 0.05)',
      md: '0 8px 25px rgba(0, 0, 0, 0.08)',
      lg: '0 10px 25px rgba(218, 119, 86, 0.18)', // accent(coral) 기반 glow
    },
  },

  // z-index 스케일 — 새 컴포넌트는 반드시 이 토큰을 사용하고 임의 숫자 박지 말 것
  zIndex: {
    base: 1,           // 기본 흐름
    raised: 10,        // 카드 hover, 미세한 elevation
    dropdown: 100,     // 페이지 내 dropdown/select
    sticky: 1000,      // 헤더, sticky 탭
    overlay: 1200,     // 모달 dim 배경
    modal: 1300,       // 모달 컨텐츠
    popover: 1400,     // 모달 위에 뜨는 select dropdown 등
    toast: 1500,       // 항상 최상단
  },

  // 카드/elevation 토큰 — 2025 트렌드 (border 제거 + soft shadow + soft-radius)
  card: {
    radius: '16px',                                          // 새 카드는 16~20px
    radiusLarge: '20px',                                     // hero/feature 카드
    background: claudeTokens.color.surface,
    border: 'none',                                          // border 대신 shadow
    shadow: '0 2px 8px rgba(20, 20, 40, 0.04)',              // 기본
    shadowHover: '0 8px 24px rgba(20, 20, 40, 0.08)',        // hover elevate
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  },

  // 공통 스타일
  styles: {
    borderRadius: {
      sm: '8px',
      md: '16px',
      lg: '20px',
    },

    spacing: {
      xs: '8px',
      sm: '16px',
      md: '24px',
      lg: '32px',
      xl: '48px',
    },

    typography: {
      fontSize: {
        xs: '12px',
        sm: '14px',
        base: '16px',
        lg: '18px',
        xl: '24px',
        '2xl': '32px',
      },
      fontWeight: {
        normal: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
      },
    },

    transitions: {
      base: 'all 0.3s ease',
      fast: 'all 0.2s ease',
    },
  },
};

export const theme = createTheme({
    typography: {
        fontFamily: claudeTokens.font.body,
        h1: {
            fontSize: '2.5rem',
            fontWeight: 'bold',
        },
        h2: {
            fontSize: '2.25rem',
            fontWeight: 'bold',
        },
        h3: {
            fontSize: '2rem',
            fontWeight: 'bold',
        },
        h4: {
            fontSize: '1.75rem',
            fontWeight: 'bold',
        },
        h5: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
        },
        h6: {
            fontSize: '1.25rem',
            fontWeight: 'bold',
        },
        body1: {
            fontSize: '0.90rem',
        },
        body2: {
            fontSize: '0.80rem',
        },
    },
    palette: {
        common: {
            black: '#000',
            white: '#fff',
        },
        primary: {
            main: claudeTokens.color.accent,
            dark: claudeTokens.color.accentDark,
            light: claudeTokens.color.accentSoft,
            contrastText: '#ffffff',
        },
        background: {
            default: claudeTokens.color.bg,
            paper: claudeTokens.color.surface,
        },
        text: {
            primary: claudeTokens.color.text1,
            secondary: claudeTokens.color.text2,
        },
        success: {
            main: claudeTokens.color.status.success,
        },
        warning: {
            main: claudeTokens.color.status.warning,
        },
        error: {
            main: claudeTokens.color.status.error,
        },
        info: {
            main: claudeTokens.color.status.info,
        },
        action: {
            hover: '#f5f5f5',
            selected: '#ececec',
            disabled: '#f9f9f9',
            disabledBackground: '#f3f3f3',
        },
    },
    colors: {
        main: claudeTokens.color.accent,
        secondary: '#FF4081',
    },
    fontSizes: {
        small: '10px',
        medium: '16px',
        large: '20px',
    },
});

export type DesignSystem = typeof designSystem;
